/*
 * @Description: 实时定位驾驶舱 — 右侧「实时动态」卡片（历史态标题为「当日动态」）
 *   事件按时间倒序；支持按事件类型单选筛选；事件项不可点击。
 *   后端事件 desc 多为设备视角长文案，前端按事件类型规整为简洁文案，对齐设计稿。
 */
import React from 'react'
import { Select } from 'antd'
import {
  DownOutlined,
  EnvironmentOutlined,
  UpOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { CockpitEventItem } from '../../types/cockpit.types'

const { Option } = Select

/** 事件类型筛选项（all = 全部事件） */
const EVENT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: '全部事件' },
  { value: 'photo', label: '拍照' },
  { value: 'stay_timeout', label: '停留超限' },
  { value: 'continuous_distance', label: '连续移动超距' },
  { value: 'offline', label: '离线' },
  { value: 'online', label: '重新上线' },
  { value: 'deep_sleep', label: '低电休眠' }
]

/** 事件类型 → 时间轴圆点颜色 */
const EVENT_DOT_COLOR: { [key: string]: string } = {
  photo: '#1890ff',
  stay_timeout: '#eb2f96',
  continuous_distance: '#52c41a',
  offline: '#bfbfbf',
  online: '#52c41a',
  low_battery: '#faad14',
  deep_sleep: '#faad14'
}

/**
 * 描述跟在昵称后同一行的事件类型（文案简洁的几类）。
 * 其余类型（停留超限 / 连续移动超距 / 低电量等较长文案）描述另起一行，避免挤压昵称。
 */
const INLINE_DESC_TYPES = new Set(['photo', 'offline', 'online', 'deep_sleep'])

/** 滚动到距底部多少像素时触发加载下一页 */
const LOAD_MORE_THRESHOLD_PX = 60

/**
 * 把后端事件规整为简洁展示文案（对齐设计稿）。
 * 离线/上线后端为设备视角长文案（"设备 862... 超过 5 分钟未上报"），统一收敛为简洁标签；
 * 拍照按张数；停留/超距优先用后端 desc。
 */
function formatEventDesc(event: CockpitEventItem): string {
  switch (event.type) {
    case 'photo':
      return `拍照 ${event.photoCount || 1} 张`
    case 'offline':
      return '离线'
    case 'online':
      return '重新上线'
    case 'deep_sleep':
      return '低电休眠'
    case 'stay_timeout':
      return event.desc || '停留超限'
    case 'continuous_distance':
      return event.desc || '连续移动超距'
    default:
      return event.desc || ''
  }
}

interface RealtimeEventsCardProps {
  /** C1 事件列表 */
  events: CockpitEventItem[]
  /** 卡片标题（实时态「实时动态」/ 历史态「当日动态」） */
  title: string
  /** 当前事件类型筛选 */
  typeFilter: string
  /** 是否收起 */
  collapsed: boolean
  /** 是否还有下一页 */
  hasMore: boolean
  /** 事件类型筛选变化 */
  onChangeType: (type: string) => void
  /** 滑到底部加载下一页 */
  onLoadMore: () => void
  /** 切换收起/展开 */
  onToggleCollapse: () => void
}

/** 单事件行 */
function EventRow({ event }: { event: CockpitEventItem }) {
  const shortName = (event.userName || '').slice(-2)
  const dotColor = EVENT_DOT_COLOR[event.type] || '#bfbfbf'
  const desc = formatEventDesc(event)
  const isOffline = event.type === 'offline'
  const isOnline = event.type === 'online'
  const hasIdentity = !!(event.avatar || event.userName)
  // 非 5 类（如低电量）描述另起一行
  const descBelow = !INLINE_DESC_TYPES.has(event.type)

  return (
    <div className="cockpit-event-row">
      <div className="cockpit-event-row__time">
        {dayjs(event.time).format('HH:mm')}
      </div>
      <span
        className="cockpit-event-row__dot"
        style={{ background: dotColor }}
      />
      <div className="cockpit-event-row__main">
        {hasIdentity && (
          <span className="cockpit-event-row__avatar">
            {event.avatar ? (
              <img src={event.avatar} alt="" />
            ) : (
              <span className="cockpit-event-row__initial">{shortName}</span>
            )}
          </span>
        )}
        <div className="cockpit-event-row__text">
          <div className="cockpit-event-row__line1">
            {!!event.userName && (
              <span className="cockpit-event-row__name">{event.userName}</span>
            )}
            {!descBelow && (
              <span
                className={`cockpit-event-row__desc ${
                  isOffline ? 'is-offline' : ''
                } ${isOnline ? 'is-online' : ''}`}
              >
                {desc}
              </span>
            )}
          </div>

          {descBelow && (
            <div
              className={`cockpit-event-row__desc cockpit-event-row__desc--below ${
                isOffline ? 'is-offline' : ''
              }`}
            >
              {desc}
            </div>
          )}

          {!!event.address && (
            <div className="cockpit-event-row__addr">
              <EnvironmentOutlined />{' '}
              {isOffline ? `最后位置：${event.address}` : event.address}
            </div>
          )}

          {event.type === 'photo' &&
            event.thumbnails &&
            event.thumbnails.length > 0 && (
              <div className="cockpit-event-row__photos">
                {event.thumbnails.slice(0, 3).map((url, idx) => (
                  <span className="cockpit-event-row__thumb" key={idx}>
                    <img src={url} alt="" />
                  </span>
                ))}
                {!!event.photoCount && event.photoCount > 3 && (
                  <span className="cockpit-event-row__more">
                    共 {event.photoCount} 张
                  </span>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

const RealtimeEventsCard: React.FC<RealtimeEventsCardProps> = ({
  events,
  title,
  typeFilter,
  collapsed,
  hasMore,
  onChangeType,
  onLoadMore,
  onToggleCollapse
}) => {
  // 滚动到接近底部时加载下一页
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (
      hasMore &&
      el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX
    ) {
      onLoadMore()
    }
  }

  return (
    <div className={`cockpit-card ${collapsed ? 'is-collapsed' : ''}`}>
      <div className="cockpit-card__header" onClick={onToggleCollapse}>
        <span className="cockpit-card__title">{title}</span>
        <span className="cockpit-card__toggle">
          {collapsed ? '展开' : '收起'}
          {collapsed ? <DownOutlined /> : <UpOutlined />}
        </span>
      </div>

      {!collapsed && (
        <div className="cockpit-card__body" onScroll={handleScroll}>
          <div className="cockpit-event-filter">
            <Select
              size="small"
              value={typeFilter}
              style={{ width: 140 }}
              onChange={onChangeType}
            >
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          {events.map((event, idx) => (
            <EventRow
              key={`${event.userId}-${event.time}-${idx}`}
              event={event}
            />
          ))}

          {events.length === 0 && (
            <div className="cockpit-card__empty">暂无动态</div>
          )}

          {events.length > 0 &&
            (hasMore ? (
              <div className="cockpit-card__loadmore">加载中…</div>
            ) : (
              <div className="cockpit-card__loadmore cockpit-card__loadmore--end">
                完
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

export default RealtimeEventsCard
